import { useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import {
  IntegrationServiceType,
  ListTenantIntegrationsQuery,
  ListTenantIntegrationsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { FundraisingAnalytics } from "~frontend/components/analyticsDashboard/FundraisingAnalytics";
import { OrganizingAnalytics } from "~frontend/components/analyticsDashboard/OrganizingAnalytics";
import {
  StyledTabs,
  StyledTab,
} from "~frontend/components/analyticsDashboard/StyledTabs";
import { LIST_TENANT_INTEGRATIONS } from "~frontend/graphql/integration";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { palette } from "~frontend/theme";

export default function OrgAnalytics(): ReactElement {
  useBreadcrumbs([
    {
      label: "Organization Analytics",
      path: "/organization/analytics",
    },
  ]);

  const { data, loading, error } = useQuery<
    ListTenantIntegrationsQuery,
    ListTenantIntegrationsQueryVariables
  >(LIST_TENANT_INTEGRATIONS, {
    variables: {
      input: {
        service: IntegrationServiceType.ActBlueWebhook,
      },
    },
  });

  const hasActBlueIntegration = !!data?.listTenantIntegrations?.length;

  const [activeTab, setActiveTab] = useQueryParam(
    "tab",
    withDefault(
      StringParam,
      hasActBlueIntegration ? "fundraising" : "organizing"
    )
  );

  return (
    <PageContainer>
      <PageHeader>Analytics</PageHeader>
      <AsyncWrapper loading={loading} errors={error}>
        {hasActBlueIntegration ? (
          <StyledTabs
            value={activeTab}
            onChange={(_evt, val) => setActiveTab(val)}
            variant="fullWidth"
          >
            <StyledTab
              label={
                <FormattedMessage
                  defaultMessage="Fundraising"
                  description="Tab header for the fundraising analytics page"
                />
              }
              value={"fundraising"}
            />
            <StyledTab
              label={
                <FormattedMessage
                  defaultMessage="Organizing"
                  description="Tab header for the organizing analytics page"
                />
              }
              value={"organizing"}
            />
          </StyledTabs>
        ) : null}
        <Box
          sx={{
            padding: "40px",
            backgroundColor: palette.lightGray,
          }}
        >
          <FundraisingAnalytics visible={activeTab === "fundraising"} />
          <OrganizingAnalytics visible={activeTab === "organizing"} />
        </Box>
      </AsyncWrapper>
    </PageContainer>
  );
}
